/**
 * CreateProjectModal Component for LOOPINO AI Platform
 * Modal per creare o modificare album AI Storage da mobile
 */

import React, { useState, useCallback, useEffect } from 'react';
import './CreateProjectModal.css';
import useLanguage from '../../../hooks/useLanguage';
import aiStorageService from '../../../services/AIStorageService';

// Import icons
import iconClose from '../../../assets/images/icons/icon-close.svg';
import iconAI from '../../../assets/images/icons/icon-ai.svg';

/**
 * FileDescriptionModal Component for editing file descriptions
 */
export const FileDescriptionModal = ({
  isOpen,
  onClose,
  onFileUpdated,
  file = null,
}) => {
  const { t } = useLanguage();
  const [description, setDescription] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && file) {
      setDescription(file.description || '');
      setError(null);
    }
    if (!isOpen) {
      setDescription('');
      setIsUpdating(false);
      setError(null);
    }
  }, [isOpen, file]);

  const handleDescriptionChange = useCallback((event) => {
    setDescription(event.target.value);
    setError(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!file?.id) {
      setError(t('aiStorage.modals.fileDescription.errors.invalidFile'));
      return;
    }

    try {
      setIsUpdating(true);
      setError(null);

      const normalized = await aiStorageService.updateAsset(file.id, {
        description: description.trim() || null,
      });

      if (onFileUpdated) {
        onFileUpdated(normalized);
      }

      onClose();
    } catch (err) {
      console.error('[FileDescriptionModal] Failed to update media file', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.modals.fileDescription.errors.update', { error: message }));
    } finally {
      setIsUpdating(false);
    }
  }, [description, file, onClose, onFileUpdated, t]);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget && !isUpdating) {
      onClose();
    }
  }, [isUpdating, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="mobile-create-project-modal" onClick={handleBackdropClick}>
      <div className="mobile-create-project-content">
        <div className="mobile-create-project-header">
          <div className="mobile-create-project-title-section">
            <div className="mobile-create-project-icon">
              <img src={iconAI} alt={t('aiStorage.modals.fileDescription.alt.icon')} />
            </div>
            <h2 className="mobile-create-project-title">{t('aiStorage.modals.fileDescription.title')}</h2>
          </div>
          <button
            className="mobile-create-project-close"
            onClick={onClose}
            disabled={isUpdating}
          >
            <img src={iconClose} alt={t('aiStorage.common.alt.close')} />
          </button>
        </div>

        {error && (
          <div className="mobile-create-project-error">
            {error}
          </div>
        )}

        <div className="mobile-create-project-body">
          <div className="mobile-create-project-section">
            <label className="mobile-create-project-label" htmlFor="file-description-input">
              {t('aiStorage.modals.fileDescription.label')}
            </label>
            <textarea
              id="file-description-input"
              className="mobile-create-project-textarea"
              value={description}
              onChange={handleDescriptionChange}
              placeholder={t('aiStorage.modals.fileDescription.placeholder')}
              rows={4}
              disabled={isUpdating}
            />
          </div>
        </div>

        <div className="mobile-create-project-actions">
          <button
            className="mobile-create-project-btn secondary"
            onClick={onClose}
            disabled={isUpdating}
          >
            {t('aiStorage.actions.cancel')}
          </button>
          <button
            className="mobile-create-project-btn primary"
            onClick={handleSave}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <div className="mobile-create-project-btn-loading">
                <div className="mobile-create-project-spinner" />
                <span>{t('aiStorage.status.saving')}</span>
              </div>
            ) : (
              t('aiStorage.actions.save')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateProjectModal = ({
  isOpen,
  onClose,
  onProjectCreated,
  editMode = false,
  existingProject = null,
}) => {
  const { t } = useLanguage();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setTitle(existingProject?.title || existingProject?.name || '');
      setDescription(existingProject?.description || existingProject?.aiGeneratedDescription || '');
      setError(null);
    } else {
      setTitle('');
      setDescription('');
      setIsSaving(false);
      setError(null);
    }
  }, [isOpen, existingProject, editMode]);

  const handleTitleChange = useCallback((event) => {
    setTitle(event.target.value);
    setError(null);
  }, []);

  const handleDescriptionChange = useCallback((event) => {
    setDescription(event.target.value);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    if (!isSaving) {
      onClose();
    }
  }, [isSaving, onClose]);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  const handleSubmit = useCallback(async () => {
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();

    if (!trimmedTitle) {
      setError(t('aiStorage.modals.create.errors.missingTitle'));
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      let project;
      if (editMode && existingProject?.id) {
        project = await aiStorageService.updateProject(existingProject.id, {
          title: trimmedTitle,
          description: trimmedDescription || null,
        });
      } else {
        project = await aiStorageService.createProject({
          title: trimmedTitle,
          description: trimmedDescription || null,
        });
      }

      if (onProjectCreated) {
        onProjectCreated(project);
      }

      onClose();
    } catch (err) {
      console.error('[CreateProjectModal] Failed to save album', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(
        editMode
          ? t('aiStorage.modals.create.errors.update', { error: message })
          : t('aiStorage.modals.create.errors.create', { error: message })
      );
    } finally {
      setIsSaving(false);
    }
  }, [description, editMode, existingProject, onClose, onProjectCreated, t, title]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="mobile-create-project-modal" onClick={handleBackdropClick}>
      <div className="mobile-create-project-content">
        <div className="mobile-create-project-header">
            <div className="mobile-create-project-title-section">
              <div className="mobile-create-project-icon">
                <img src={iconAI} alt={t('aiStorage.modals.create.alt.icon')} />
              </div>
              <h2 className="mobile-create-project-title">
              {editMode ? t('aiStorage.modals.create.title.edit') : t('aiStorage.modals.create.title.create')}
              </h2>
            </div>
            <button
              className="mobile-create-project-close"
              onClick={handleClose}
              disabled={isSaving}
            >
              <img src={iconClose} alt={t('aiStorage.common.alt.close')} />
            </button>
          </div>

        {error && (
          <div className="mobile-create-project-error">
            {error}
          </div>
        )}

        <div className="mobile-create-project-body">
          <div className="mobile-create-project-section">
            <label className="mobile-create-project-label" htmlFor="album-title-input">
              {t('aiStorage.modals.create.labels.title')}
            </label>
            <input
              id="album-title-input"
              className="mobile-create-project-input"
              value={title}
              onChange={handleTitleChange}
              placeholder={t('aiStorage.modals.create.placeholders.title')}
              disabled={isSaving}
            />
          </div>

          <div className="mobile-create-project-section">
            <label className="mobile-create-project-label" htmlFor="album-description-input">
              {t('aiStorage.modals.create.labels.description')}
            </label>
            <textarea
              id="album-description-input"
              className="mobile-create-project-textarea"
              value={description}
              onChange={handleDescriptionChange}
              placeholder={t('aiStorage.modals.create.placeholders.description')}
              rows={4}
              disabled={isSaving}
            />
          </div>
        </div>

        <div className="mobile-create-project-actions">
          <button
            className="mobile-create-project-btn secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            {t('aiStorage.actions.cancel')}
          </button>
          <button
            className="mobile-create-project-btn primary"
            onClick={handleSubmit}
            disabled={isSaving || !title.trim()}
          >
            {isSaving ? (
              <div className="mobile-create-project-btn-loading">
                <div className="mobile-create-project-spinner" />
                <span>{editMode ? t('aiStorage.status.updating') : t('aiStorage.status.creating')}</span>
              </div>
            ) : (
              editMode ? t('aiStorage.actions.update') : t('aiStorage.actions.create')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;
